import { workAreaCategoryForKey } from '@vivaporta/work-areas';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import type { LandingLocale } from '../../shared/content/landingContentTypes.js';
import { industryPageKeys, isIndustryPageKey } from '../../shared/content/industryPages.js';
import type { StaticPageAudience, StaticPageContent, StaticPageKey } from '../../shared/content/staticPageContent.js';
import { openCompanyContactDialog } from './navigation.js';
import { highlightedHeading } from '../components/highlightedHeading.js';
import { industryHeroPresentation } from '../../shared/content/industryHeroPresentation.js';
import { editorSemanticText } from '../editor/editorSemanticText.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

export const industryHeroPageKeys: readonly StaticPageKey[] = industryPageKeys;
export const isIndustryHeroPage = (key: StaticPageKey) => isIndustryPageKey(key);

interface IndustryHeroOptions {
  pageKey: StaticPageKey;
  page: StaticPageContent;
  locale: LandingLocale;
  audience: StaticPageAudience;
  editor?: LandingEditorRenderOptions;
  sectionStyle?: string;
}

const galleryUi = {
  de: { gallery: 'Einblicke in die Branche', image: 'Bild' },
  en: { gallery: 'Industry insights', image: 'Image' },
  tr: { gallery: 'Sektörden görüntüler', image: 'Görsel' },
};

const hoverTimers = new WeakMap<HTMLElement, number>();
const pointerTypes = new WeakMap<HTMLElement, string>();
const cancelHover = (card: HTMLElement) => {
  window.clearTimeout(hoverTimers.get(card));
  hoverTimers.delete(card);
};
const activateOnHover = (event: PointerEvent, activate: () => void) => {
  const card = event.currentTarget as HTMLElement;
  if (event.pointerType !== 'mouse' || hoverTimers.has(card)) return;
  hoverTimers.set(card, window.setTimeout(() => {
    hoverTimers.delete(card);
    if (card.isConnected && card.matches(':hover')) activate();
  }, 160));
};

export const industryHeroSection = stateful(({ pageKey, page, locale, audience, editor, sectionStyle }: IndustryHeroOptions) => {
  const prefix = `static.${pageKey}.hero`;
  const appearance = industryHeroPresentation(page, locale);
  const [activeIndex, setActiveIndex] = useState(0);
  const gallery = page.heroGallery?.length
    ? page.heroGallery
    : page.heroImage
      ? [{ image: page.heroImage, imageAlt: page.title, title: page.eyebrow }]
      : [];
  // Inspector selection brings the corresponding image or caption to the front.
  const selectedIndex =
    editor?.mode === 'editor'
      ? gallery.findIndex((_, index) => editor.selectedElementId?.startsWith(`${prefix}.portrait.${index}`))
      : -1;
  const active = selectedIndex >= 0 ? selectedIndex : activeIndex % Math.max(1, gallery.length);
  const move = (direction: number) =>
    setActiveIndex((active + direction + gallery.length) % Math.max(1, gallery.length));
  const ui = galleryUi[locale];
  const editableLabel = (value: string, id: string, className = '') =>
    html`<span
      class=${editorNodeClass(editor, id, className)}
      style=${editorNodeStyle(editor, id)}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, id, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, id, event)}
      >${value}</span
    >`;
  const category = workAreaCategoryForKey(pageKey);
  const tone = pageKey === 'gastro' ? 'gastro' : category === 'pflege' ? 'care' : category === 'einzelhandel' ? 'retail' : 'craft';

  return html` <section
    class=${editorNodeClass(editor, prefix, `vp-industry-hero vp-industry-hero--${tone}`)}
    style=${sectionStyle ?? editorNodeStyle(editor, prefix)}
    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, prefix, event)}
    @click=${(event: MouseEvent) => editorNodeClick(editor, prefix, event)}
  >
    <div
      class=${editorNodeClass(editor, `${prefix}.backgroundWord`, 'vp-team-hero-word vp-industry-hero-word')}
      style=${`--industry-word-characters: ${Math.max(4, appearance.heroBackgroundWord?.length ?? 4)}; ${editorNodeStyle(editor, `${prefix}.backgroundWord`)}`}
      aria-hidden="true"
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.backgroundWord`, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.backgroundWord`, event)}
    >${appearance.heroBackgroundWord}</div>
    <div class="vp-team-hero-lines vp-industry-hero-lines" aria-hidden="true"><span></span><span></span><span></span></div>
    <div
      class=${editorNodeClass(editor, `${prefix}.layout`, 'vp-shell vp-industry-hero-layout')}
      style=${editorNodeStyle(editor, `${prefix}.layout`)}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.layout`, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.layout`, event)}
    >
      ${gallery.length
        ? html` <div
            class=${editorNodeClass(editor, `${prefix}.gallery`, 'vp-industry-gallery')}
            style=${editorNodeStyle(editor, `${prefix}.gallery`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.gallery`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.gallery`, event)}
          >
            <div
              class="vp-industry-wallet"
              role="group"
              aria-label=${ui.gallery}
              @keydown=${(event: KeyboardEvent) => {
                if (editor?.mode === 'editor' || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
                event.preventDefault();
                const direction = event.key === 'ArrowRight' ? 1 : -1;
                move(direction);
                const next = (active + direction + gallery.length) % gallery.length;
                (event.currentTarget as HTMLElement)
                  .querySelectorAll<HTMLButtonElement>('.vp-industry-photo-card')
                  [next]?.focus({ preventScroll: true });
              }}
            >
              ${gallery.map((photo, index) => {
                const id = `${prefix}.portrait.${index}`;
                return html` <button
                  type="button"
                  class=${editorNodeClass(editor, id, `vp-industry-photo-card ${index === active ? 'is-active' : ''}`)}
                  style=${editorNodeStyle(editor, id)}
                  aria-label=${`${ui.image} ${index + 1}: ${photo.imageAlt}`}
                  aria-pressed=${String(index === active)}
                  tabindex=${index === active || editor?.mode === 'editor' ? '0' : '-1'}
                  @pointermove=${(event: PointerEvent) => {
                    // Actual pointer movement avoids repeated swaps when the stacked cards move under a resting cursor.
                    if (editor?.mode !== 'editor' && index !== active)
                      activateOnHover(event, () => setActiveIndex(index));
                  }}
                  @pointerleave=${(event: PointerEvent) => cancelHover(event.currentTarget as HTMLElement)}
                  @pointerdown=${(event: PointerEvent) => {
                    const card = event.currentTarget as HTMLElement;
                    cancelHover(card);
                    pointerTypes.set(card, event.pointerType);
                    editorNodePointerDown(editor, id, event);
                  }}
                  @click=${(event: MouseEvent) => {
                    if (editor?.mode === 'editor') {
                      editorNodeClick(editor, id, event);
                      return;
                    }
                    const touchOrKeyboard = event.detail === 0 || pointerTypes.get(event.currentTarget as HTMLElement) !== 'mouse';
                    setActiveIndex(index === active && touchOrKeyboard ? (index + 1) % gallery.length : index);
                  }}
                >
                  <img
                    class=${editorNodeClass(editor, `${id}.image`, 'vp-industry-photo')}
                    style=${editorNodeStyle(editor, `${id}.image`)}
                    src=${photo.image}
                    alt=${photo.imageAlt}
                    width="1100"
                    height="825"
                    loading=${index === 0 ? 'eager' : 'lazy'}
                    fetchpriority=${index === 0 ? 'high' : 'auto'}
                    decoding="async"
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${id}.image`, event)}
                    @click=${(event: MouseEvent) => editorNodeClick(editor, `${id}.image`, event)}
                  />
                  ${photo.title ? editableLabel(photo.title, `${id}.title`, 'vp-industry-photo-caption') : nothing}
                </button>`;
              })}
            </div>
          </div>`
        : nothing}
      <div
        class=${editorNodeClass(editor, `${prefix}.copy`, 'vp-industry-hero-copy')}
        style=${editorNodeStyle(editor, `${prefix}.copy`)}
        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.copy`, event)}
        @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.copy`, event)}
      >
        ${highlightedHeading({
          editor,
          titleElementId: `${prefix}.title`,
          highlightElementId: `${prefix}.titleHighlight`,
          headingTag: 'h1',
          className: 'vp-industry-title',
          title: page.title,
          highlight: appearance.heroTitleHighlight,
        })}
        ${editorSemanticText({
          editor,
          elementId: `${prefix}.intro`,
          fallbackTag: 'p',
          className: 'vp-industry-intro',
          content: page.intro,
        })}
        ${page.heroHighlights?.length
          ? html`<ul class="vp-industry-highlights">
              ${page.heroHighlights.map(
                (text, index) => html`
                  <li>
                    <span class="icon-[material-symbols--check-circle-rounded]" aria-hidden="true"></span>
                    ${editableLabel(text, `${prefix}.heroHighlight.${index}`)}
                  </li>
                `,
              )}
            </ul>`
          : nothing}
        ${page.heroAction
          ? audience === 'company'
            ? html`<button
                type="button"
                class=${editorNodeClass(editor, `${prefix}.heroAction.label`, 'vp-action vp-action-primary vp-industry-action')}
                style=${editorNodeStyle(editor, `${prefix}.heroAction.label`)}
                aria-haspopup="dialog"
                aria-controls="vp-contact-modal"
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.heroAction.label`, event)}
                @click=${(event: MouseEvent) => {
                  if (editor?.mode === 'editor') {
                    editorNodeClick(editor, `${prefix}.heroAction.label`, event);
                    return;
                  }
                  event.stopPropagation();
                  openCompanyContactDialog((event.currentTarget as HTMLElement).closest('.vp-page'));
                }}
              >${page.heroAction.label}<span class="icon-[material-symbols--arrow-forward-rounded]" aria-hidden="true"></span></button>`
            : html`<a
              class=${editorNodeClass(
                editor,
                `${prefix}.heroAction.label`,
                'vp-action vp-action-primary vp-industry-action',
              )}
              style=${editorNodeStyle(editor, `${prefix}.heroAction.label`)}
              href=${page.heroAction.href}
              @pointerdown=${(event: PointerEvent) =>
                editorNodePointerDown(editor, `${prefix}.heroAction.label`, event)}
              @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.heroAction.label`, event)}
            >
              ${page.heroAction.label}<span
                class="icon-[material-symbols--arrow-forward-rounded]"
                aria-hidden="true"
              ></span>
            </a>`
          : nothing}
      </div>
    </div>
  </section>`;
});
